import { useEffect, useRef, useState } from "react";
import abcjs, { type NoteTimingEvent, type TuneObject } from "abcjs";

/** Portal-owned playback view; the vendored renderer stays a copy of upstream. */
interface Props {
  abc: string;
  single: boolean;
  currentCell: number | null;
  totalCells: number;
}

type PositionedEvent = NoteTimingEvent & { left: number; top: number; height: number };

function hasPosition(event: NoteTimingEvent): event is PositionedEvent {
  return event?.type === "event" && Number.isFinite(event.milliseconds) &&
    Number.isFinite(event.left) && Number.isFinite(event.top) && Number.isFinite(event.height);
}

function cursorPosition(events: NoteTimingEvent[], cell: number, cells: number) {
  if (!Number.isInteger(cells) || cells <= 0 || !Number.isInteger(cell) || cell < 0 || cell >= cells) return null;
  const duration = events.at(-1)?.milliseconds ?? 0;
  if (!Number.isFinite(duration) || duration <= 0) return null;
  // Normalise the engraving's timeline: abcjs counts compound beats differently
  // from the drum sequencer. A fraction of the full pattern works for both.
  // This is a position in the written score, not a playback clock. onStep
  // already arrives at the swung audio time; shifting the position again
  // would put the line past the written note when that note sounds.
  const time = duration * cell / cells;
  const positions = events.filter(hasPosition);
  let index = 0;
  while (index + 1 < positions.length && positions[index + 1].milliseconds <= time + 1) index++;
  const event = positions[index];
  if (!event) return null;
  const next = positions[index + 1];
  const endTime = next?.milliseconds ?? duration;
  const endX = next?.line === event.line ? next.left : event.endX ?? event.left;
  const fraction = endTime > event.milliseconds
    ? Math.max(0, Math.min(1, (time - event.milliseconds) / (endTime - event.milliseconds)))
    : 0;
  return {
    x: event.left + (endX - event.left) * fraction - 2,
    top: event.top - 3,
    bottom: event.top + event.height + 3,
  };
}

/** Keep the same X-head stem correction as the vendored Notation view. */
export function trimXHeadStems(container: HTMLElement): void {
  for (const note of container.querySelectorAll<SVGGElement>("g.abcjs-note")) {
    const heads = [...note.querySelectorAll<SVGPathElement>("path.abcjs-notehead")];
    if (heads.length === 0) continue;
    const boxes = heads.map((head) => ({ head, box: head.getBBox() }));
    const lowest = boxes.reduce((a, b) => a.box.y + a.box.height > b.box.y + b.box.height ? a : b);
    const highest = boxes.reduce((a, b) => a.box.y < b.box.y ? a : b);
    for (const stem of note.querySelectorAll<SVGPathElement>("path.abcjs-stem")) {
      const corners = /^M ([\d.-]+) ([\d.-]+)L [\d.-]+ ([\d.-]+)L ([\d.-]+) /.exec(stem.getAttribute("d") || "");
      if (!corners) continue;
      const x1 = Number(corners[1]);
      const x2 = Number(corners[4]);
      const top = Math.min(Number(corners[2]), Number(corners[3]));
      const bottom = Math.max(Number(corners[2]), Number(corners[3]));
      const isUp = Math.abs(bottom - (lowest.box.y + lowest.box.height / 2)) <
        Math.abs(top - (highest.box.y + highest.box.height / 2));
      const head = isUp ? lowest : highest;
      if (!(head.head.getAttribute("d") || "").includes("l 1.86 1.5")) continue;
      const newTop = isUp ? top : highest.box.y + highest.box.height - 0.8;
      const newBottom = isUp ? lowest.box.y + 0.8 : bottom;
      if (newBottom > newTop) {
        stem.setAttribute("d", `M ${x1} ${newTop}L ${x1} ${newBottom}L ${x2} ${newBottom}L ${x2} ${newTop}z`);
      }
    }
  }
}

export function PlaybackNotation({ abc, single, currentCell, totalCells }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const timing = useRef<NoteTimingEvent[]>([]);
  const cursor = useRef<SVGLineElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    cursor.current = null;
    timing.current = [];
    let tune: TuneObject | undefined;
    try {
      tune = abcjs.renderAbc(ref.current, abc, {
        add_classes: true,
        responsive: "resize",
        staffwidth: 700,
        paddingtop: 8,
        paddingbottom: 8,
      })[0];
      trimXHeadStems(ref.current);
      setFailed(false);
    } catch {
      ref.current.replaceChildren();
      setFailed(true);
      return;
    }

    // The score remains useful if the optional playhead cannot be prepared.
    // Keep this separate from the engraving's failure state.
    if (!tune) return;
    try {
      tune.setTiming(60);
      // abcjs exposes noteTimings at runtime after setTiming; its TuneObject
      // declaration omits it. Tests use the real engraver to pin this boundary.
      const events = (tune as TuneObject & { noteTimings?: NoteTimingEvent[] | null }).noteTimings ?? [];
      timing.current = Array.isArray(events) ? events : [];
      if (timing.current.length === 0) return;
      const svg = ref.current.querySelector("svg");
      if (svg) {
        const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
        line.setAttribute("class", "groove-notation-playhead");
        line.setAttribute("aria-hidden", "true");
        line.setAttribute("visibility", "hidden");
        line.setAttribute("stroke", "#dc2626");
        line.setAttribute("stroke-width", "1.5");
        line.setAttribute("vector-effect", "non-scaling-stroke");
        line.setAttribute("pointer-events", "none");
        cursor.current = line;
        svg.append(line);
      }
    } catch {
      cursor.current?.remove();
      cursor.current = null;
      timing.current = [];
    }
  }, [abc]);

  useEffect(() => {
    const line = cursor.current;
    if (!line) return;
    try {
      const position = currentCell === null ? null : cursorPosition(timing.current, currentCell, totalCells);
      line.setAttribute("visibility", position ? "visible" : "hidden");
      if (position) {
        line.setAttribute("x1", String(position.x));
        line.setAttribute("x2", String(position.x));
        line.setAttribute("y1", String(position.top));
        line.setAttribute("y2", String(position.bottom));
      }
    } catch {
      // An unexpected timing payload or paint failure must not unmount practice.
      line.remove();
      cursor.current = null;
      timing.current = [];
    }
  }, [abc, currentCell, totalCells]);

  return <div
    className={single ? "notation single-measure" : "notation"}
    ref={ref}
    role={failed ? "status" : undefined}
    aria-live={failed ? "polite" : undefined}
  >
    {failed ? "Notation could not be drawn. The groove can still be played and edited." : null}
  </div>;
}
